import React, { useEffect, useState } from 'react';
import { createSelector } from 'reselect';
import styled, { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import { joyDocHelper } from '@builttocreate/engine-utils';
import { applyLogic } from '@joyfill/conditional-logic';
import { PDFRenderer } from '@joyfill/layouts';
import Field from '../fields/Field';
import ExportFormPage from './ExportFormPage';
import { formulas } from '../formulas';
import {
  calculatePagePercentageAdjustments,
  reversePercentageAdjustedValue,
  getFormattedFieldPositionForMerging,
  migrateCollectionFieldRowDecoratorsToRootSchema,
  getTableFormattedPageFields,
  isFieldHiddenInView,
  getFileOrientation,
  getOrientationDimensions,
} from '../../../lib/documentHelper';

import { resolveMeasuredFieldHeight, resolveAutoHeightExportChanges, getExporterMeasurableFields } from '../../../lib/exporterHelper';

import { validateLicense } from '../../../lib/licenseValidator.js';

import { ThemeProvider } from '../../../theme/ThemeContext';
import getThemeObject, { getFieldThemeObject } from '../../../theme/getThemeObject';

import { Red } from '../../../constants/Colors';
import FieldModes from '../../../constants/FieldModes';
import FieldTypes from '../../../constants/FieldTypes';
import FileViews from '../../../constants/FileViews';
import PDFRendererFieldLayoutTypes from '../../../constants/PDFRenderFieldLayoutTypes';
import FieldPresentationModes from '../../../constants/FieldPresentationModes';
import PageLayoutModes from '../../../constants/PageLayoutModes';

const { getPageOrder, sortPages, getCleanedJoyDoc } = joyDocHelper;

/**
 * Selector that cleans the document and resolves any formulas
 * Returns a cleaned document with all formula values calculated
 */
/**
 * @private
 */
const getCleanedAndResolvedDoc = createSelector(
  (doc) => doc,
  (doc) => {
    // First clean the document
    let cleanedDoc = getCleanedJoyDoc(doc);

    // Then resolve formulas if they exist
    if (cleanedDoc.formulas && Array.isArray(cleanedDoc.formulas) && cleanedDoc.formulas.length > 0) {
      // Check if any fields have formula applications
      const hasFieldsWithFormulas = cleanedDoc.fields.some(field =>
        field.formulas && Array.isArray(field.formulas) && field.formulas.length > 0
      );

      if (hasFieldsWithFormulas) {
        try {
          // Resolve all formulas in the document
          const formulaResult = formulas.resolve(cleanedDoc);

          if (formulaResult.status === 'valid' || formulaResult.status === 'warning') {
            // Use the resolved doc that contains updated field values
            cleanedDoc = formulaResult.doc;

            // Log warnings for debugging (non-blocking)
            if (formulaResult.warnings && formulaResult.warnings.length > 0) {
              console.warn('JoyDocExporter: Formula resolution warnings:', formulaResult.warnings);
            }
          }
        } catch (error) {
          // Log error but don't block the export
          console.error('JoyDocExporter: Error resolving formulas:', error);
        }
      }
    }

    return cleanedDoc;
  }
);

const StyledPageWrapper = styled.div`
  font-family:  ${(props) => props.theme.fontFamily};

  * {
    scrollbar-color: transparent transparent;

    &::-webkit-scrollbar {
      width: 0px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: transparent;
    }
  }
`;

/**
 * Custom shouldForwardProp function for styled-components v6 with React 19.
 * Filters out custom styled-component props from being passed to the DOM.
 * Uses @emotion/is-prop-valid as the base validation.
 */
const shouldForwardProp = (propName, target) => {
  // For custom components (functions/classes), forward all props
  if (typeof target === 'function') {
    return true;
  }
  // For string targets (DOM elements like 'div', 'span', 'svg'), use isPropValid
  if (typeof target === 'string') {
    return isPropValid(propName);
  }
  // Default: forward all props for unknown target types
  return true;
};

/**
 * Resolve the effective export page config.
 *
 * The PDF paper dimensions must match the document's orientation so landscape
 * forms export at landscape dimensions and the measurement/scaling paths use the
 * correct width/height. An explicitly provided config.page width/height is always
 * respected; otherwise the dimensions are derived from the document's orientation.
 */
const getEffectiveExportConfig = (doc, config) => {

  const padding = typeof config?.page?.padding === 'number' ? config.page.padding : 0;

  if (typeof config?.page?.width === 'number' && typeof config?.page?.height === 'number') {
    return { ...config, page: { ...config.page, padding } };
  }

  const { width, height } = getOrientationDimensions(getFileOrientation(doc?.files?.[0]));

  return { ...(config || {}), page: { ...(config?.page || {}), width, height, padding } };

};

const JoyDocExporter = ({ doc, theme, license, config }) => {

  /**
   * Don't attempty to render if doc is undefined.
   */
  if (!doc) return null;

  /**
   * Handle license validation
   */
  const [licenseValidation, setLicenseValidation] = useState(null);
  useEffect(() => {
    const handleLicenseValidation = async (license) => {
      const validation = await validateLicense(license);
      if (!validation.isValid) console.warn('LICENSE_VALIDATION_ERROR: ', validation.error);
      setLicenseValidation(validation);
    }
    handleLicenseValidation(license);
  }, []);



  /**
   * Ensure doc is a JoyDoc and notify if not.
   */
  if (doc && (!doc.files || !doc.fields)) {

    console.warn('JoyDocExporter: doc is not a valid JoyDoc JSON');

    return (
      <>
        <div style={{ textAlign: 'center', maxWidth: config?.page?.width ? `${config?.page?.width}px` : '400px' }}>
          <h1 style={{ color: Red }}>Oops. Unable to render PDF</h1>
          <p style={{ color: Red }}>Invalid JoyDoc JSON has been detected.</p>
        </div>
      </>
    );
  }

  /**
   * Resolve the effective config (orientation-aware paper dimensions). All
   * measurement/scaling below uses effectiveConfig so landscape pages export correctly.
   */
  const effectiveConfig = getEffectiveExportConfig(doc, config);

  /**
   * Warn the consumer if a config was provided but is malformed.
   */
  if (config && config.page) {
    /* eslint-disable-next-line no-console */
    if (typeof config.page?.padding !== 'number') console.warn('JoyDocExporter: config.page.padding must be a number');
  }

  // Get cleaned and formula-resolved document
  const cleanedDoc = getCleanedAndResolvedDoc(doc);

  const [measurements, setMeasurments] = React.useState({});
  useEffect(() => { setMeasurments({}); }, [doc]);

  const { pdfPages, pdfHeader, pdfFooter } = formatPagesForPDFRenderer({ doc: cleanedDoc, measurements, theme: getThemeObject(theme), config: effectiveConfig });

  /**
   * Grid pages are always measured. A float page is measured only when it carries a populated
   * auto-height field; every other float page is drawn straight from the document, as before,
   * and costs nothing extra.
   */
  const pageRequiresMeasurement = (page) => page.layout === PageLayoutModes.grid
    || getExporterMeasurableFields(page.fields, licenseValidation, page.layout).length > 0;

  const measurementRequiredPages = pdfPages.filter(pageRequiresMeasurement);

  if (!pdfPages || pdfPages.length < 1) return null;

  //If a license is passed in then we need to wait for the license validation to complete before rendering the component.
  if (license && !licenseValidation) return null;

  const pdfPageLookup = {};
  pdfPages.forEach((page) => pdfPageLookup[page._id] = page);

  const measurementCompleted = Object.keys(measurements).length === measurementRequiredPages.length;

  const pageMeasurementNodes = [];
  pdfPages.forEach((page, index) => {
    if (pageRequiresMeasurement(page)) {
      pageMeasurementNodes.push(
        <ExportFormPage
          key={index}
          {...{
            ...page,
            id: page._id,
            view: FileViews.pdf,
            licenseValidation,
            config: effectiveConfig,
            onMeasurementsCompleted: (id, pageMeasurements) => {
              if (!measurements[id]) { setMeasurments((measurements) => ({ ...measurements, [id]: pageMeasurements })); }
            }
          }}
        />
      );
    }
  });


  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <StyledPageWrapper theme={theme}>
        <ThemeProvider value={getThemeObject(theme)}>
          {!measurementCompleted ? (
            <div>
              <div style={{ textAlign: 'center', maxWidth: '400px' }}>
                <h1>Field Measuring In Progress</h1>
                <p>Element with .pdf-capture-ready will be added to the DOM when measuring is completed and PDF is ready to be captured.</p>
              </div>
              <div style={{ opacity: 1 }}>
                {pageMeasurementNodes}
              </div>
            </div>
          ) : (
            <>
              <div id="pdf-capture-ready" className="pdf-capture-ready" />
              <PDFRenderer
                {...{
                  //PDF Export Page Dimensions
                  config: effectiveConfig,
                  header: pdfHeader,
                  footer: pdfFooter,
                  pages: pdfPages,
                  fieldRenderer: (pageId, field) => (
                    <FieldRenderer
                      field={field}
                      doc={cleanedDoc}
                      licenseValidation={licenseValidation}
                      layout={pdfPageLookup[pageId]?.layout}
                      presentation={pdfPageLookup[pageId]?.presentation}
                      fieldDefaultStyles={cleanedDoc?.files[0]?.styles}

                      /**
                       * Page geometry, so FieldRenderer can express each field's size in
                       * design pixels for auto-shrink. GridLayout does the same thing for the
                       * on-screen renderer; this path does not go through GridLayout, so the
                       * conversion has to be repeated here or auto-shrink would do nothing in
                       * an export.
                       */
                      pageWidth={pdfPageLookup[pageId]?.width}
                      pageCols={pdfPageLookup[pageId]?.cols}
                      pageRowHeight={pdfPageLookup[pageId]?.rowHeight}
                    />
                  )
                }}
              />
            </>
          )}
        </ThemeProvider>
      </StyledPageWrapper>
    </StyleSheetManager>
  );
};

const FieldRenderer = ({
  field,
  doc,
  licenseValidation,
  fieldDefaultStyles,
  layout = PageLayoutModes.grid,
  presentation = FieldPresentationModes.normal,
  pageWidth,
  pageCols,
  pageRowHeight,
}) => {

  /**
   * The field's size in design pixels.
   *
   * fieldPosition.width is expressed in grid columns on an e-form page, so it has to be
   * converted against the page width before it means anything in pixels. On a PDF page cols
   * is the page width, so the same expression returns the pixel width unchanged.
   *
   * There is no viewport scaling here: the PDF renderer scales with a CSS transform on the
   * parent, so these values are already the designed ones.
   */
  const designWidth = (pageWidth && pageCols) ? (field.width * pageWidth) / pageCols : undefined;
  const designHeight = pageRowHeight ? field.height * pageRowHeight : undefined;

  return (
    <Field
      {...field}
      designWidth={designWidth}
      designHeight={designHeight}
      docFields={doc?.fields}
      view={FileViews.pdf}
      licenseValidation={licenseValidation}
      disabled
      fieldMode={FieldModes.pdf}
      fieldPresentationMode={presentation}
      pageLayoutMode={layout}
      measure //Used to disable virtualization in table and collection fields
      lazyload={false}
      fieldDefaultStyles={fieldDefaultStyles}
    />
  );
};


const formatPagesForPDFRenderer = createSelector(
  (state) => state.doc,
  (state) => state.measurements,
  (state) => state.theme,
  (state) => state.config,
  (doc, measurements, theme, config) => {

    if (!doc || !doc.fields) return [];

    const fieldLookup = {};
    doc.fields.forEach((field) => {
      if (!fieldLookup[field.file]) fieldLookup[field.file] = {};
      fieldLookup[field.file][field._id] = field;
    });

    let pdfHeader = null;
    let pdfFooter = null;
    const pdfPages = [];

    doc.files.forEach((file) => {


      /**
       * Step 1: Process Header
       */
      if (file.header) {

        let fields = [];

        const seenHeaderFPIds = {};

        file.header?.fieldPositions.forEach((fieldPosition) => {

          if (!fieldLookup[file._id][fieldPosition.field]) return;
          if (seenHeaderFPIds[fieldPosition._id]) return;
          seenHeaderFPIds[fieldPosition._id] = true;

          const targetField = fieldLookup[file._id][fieldPosition.field] || {};

          fields.push(migrateCollectionFieldRowDecoratorsToRootSchema({
            ...targetField,

            /**
             * IMPORTANT: DO NOT REMOVE.
             * - FieldPositon must come after targetField
             * - We format fieldPosition to ensure there are no conflicts with the field
             */
            ...getFormattedFieldPositionForMerging(fieldPosition),

          }));

        });

        fields = getPageFieldPositionsForPDFRenerer({
          fields,
          fieldDefaultStyles: file.styles,
          fieldMeasurements: {},
          theme
        });

        pdfHeader = {
          ...file.header,
          fields,
        };

      }

      /**
       * Step 2: Process Footer 
       */
      if (file.footer) {

        let fields = [];

        const seenFooterFPIds = {};

        file.footer?.fieldPositions.forEach((fieldPosition) => {

          if (!fieldLookup[file._id][fieldPosition.field]) return;
          if (seenFooterFPIds[fieldPosition._id]) return;
          seenFooterFPIds[fieldPosition._id] = true;

          const targetField = fieldLookup[file._id][fieldPosition.field] || {};

          fields.push(migrateCollectionFieldRowDecoratorsToRootSchema({
            ...targetField,

            /**
             * IMPORTANT: DO NOT REMOVE.
             * - FieldPositon must come after targetField
             * - We format fieldPosition to ensure there are no conflicts with the field
             */
            ...getFormattedFieldPositionForMerging(fieldPosition),

          }));

        });

        fields = getPageFieldPositionsForPDFRenerer({
          fields,
          fieldDefaultStyles: file.styles,
          fieldMeasurements: {},
          theme
        });

        pdfFooter = {
          ...file.footer,
          fields,
        };

      }
      const pages = applyLogic(
        sortPages(file.pages, getPageOrder(file.pageOrder, file.pages)),
        doc.fields,
        '_id'
      ).filter((item) => !item.hidden && !isFieldHiddenInView(item.hiddenViews, FileViews.pdf));

      pages.forEach((page) => {

        let fields = [];

        const seenPageFPIds = {};

        page.fieldPositions.forEach((fieldPosition) => {

          if (!fieldLookup[file._id][fieldPosition.field]) return;
          if (seenPageFPIds[fieldPosition._id]) return;
          seenPageFPIds[fieldPosition._id] = true;

          const targetField = fieldLookup[file._id][fieldPosition.field] || {};

          fields.push(migrateCollectionFieldRowDecoratorsToRootSchema({
            ...targetField,

            /**
             * IMPORTANT: DO NOT REMOVE.
             * - FieldPositon must come after targetField
             * - We format fieldPosition to ensure there are no conflicts with the field
             */
            ...getFormattedFieldPositionForMerging(fieldPosition),

          }));

        });

        fields = getPageFieldPositionsForPDFRenerer({
          fields,
          fieldMeasurements: measurements[page._id] || {},
          presentation: page.presentation,
          rowHeight: page.rowHeight,
          fieldDefaultStyles: file.styles,
          theme
        });

        fields = applyLogic(fields, doc.fields, '_id').filter((item) => !item.hidden && !isFieldHiddenInView(item.hiddenViews, FileViews.pdf));

        const pageDefaults = { fields, docFields: doc.fields };

        if (page.backgroundImage) {

          /**
           * Previous PDFPages used manual scaling of x, y, width, height, etc. and the PDFRenderer uses CSS scaling on the parent element
           * so everything is automatically scaled. Due to this the field fontSize is scaled differently. We need to adjust the fontSize
           * in order to match the legacy visual requirements.
           */
          const percentageDifference = calculatePagePercentageAdjustments(page.height, page.width, config.page.height, config.page.width);
          pageDefaults.fields = pageDefaults.fields.map((field) => {
            return {
              ...field,
              fontSize: reversePercentageAdjustedValue(field.fontSize || 12, percentageDifference),
            };
          });

          /**
         * We can remove padding because right now legacy simply scales the page
         * and doesn't use the padding value.
         */
          if (page.padding) pageDefaults.padding = 0;

          const defaultBackgroundSize = page.backgroundImage ? '100%' : '';
          pageDefaults.backgroundSize = page.backgroundSize ? file.backgroundSize : defaultBackgroundSize;
          pageDefaults.maxWidth = page.width;
          pageDefaults.maxHeight = page.height;
          pageDefaults.minWidth = page.width;
          pageDefaults.minHeight = page.height;
          pageDefaults.maxHeightType = 'px';
          pageDefaults.maxWidthType = 'px';
          pageDefaults.minWidthType = 'px';
          pageDefaults.minHeightType = 'px';

          /**
           * Previously the PDF Pages were using page.width as the RGL grid cols. The page.cols property
           * is not used for PDF Pages.
           */
          pageDefaults.cols = page.width;
        }

        pdfPages.push({
          ...page,
          ...pageDefaults,
        });

      });

    });

    return { pdfPages, pdfHeader, pdfFooter };

  }
);

/**
 * Handle formatting table field positions for PDF Renderer
 */
export const getTableFormattedPageFieldsForPDFRenerer = createSelector(
  getTableFormattedPageFields,
  (state) => state.fieldMeasurements,
  (fieldPositions, fieldHeightMeasurements) => {

    return fieldPositions.map((fieldPosition) => {

      const nextFieldPosition = { ...fieldPosition };
      const measurements = fieldHeightMeasurements[fieldPosition._id];

      if (fieldPosition.type === FieldTypes.collection || fieldPosition.type === FieldTypes.table ||
          (fieldPosition.type === FieldTypes.image && measurements?.layouts)) {
        nextFieldPosition.layoutType = PDFRendererFieldLayoutTypes.dynamicVertical;
        nextFieldPosition.layouts = measurements?.layouts;
      }

      return nextFieldPosition;

    });
  }
);

export const getPageFieldPositionsForPDFRenerer = createSelector(
  getTableFormattedPageFieldsForPDFRenerer,
  (state) => state.fieldMeasurements,
  (state) => state.presentation,
  (state) => state.fieldDefaultStyles,
  (state) => state.theme,
  (state) => state.rowHeight,
  (fieldPositions, fieldHeightMeasurements, fieldPresentationMode, fieldDefaultStyles, theme, rowHeight) => {

    return fieldPositions.map((fieldPosition) => {

      const fieldTheme = getFieldThemeObject({
        mode: FieldModes.readonly,
        fieldPresentationMode,
        fieldPosition,
        fileStyles: fieldDefaultStyles,
        theme: theme?.field,
        sdkTheme: theme
      });

      const nextFieldPosition = { ...fieldPosition, ...fieldTheme };

      const measurement = fieldHeightMeasurements && fieldHeightMeasurements[fieldPosition._id];

      if (measurement && Number.isFinite(measurement.autoHeightOverflowPx)) {

        /**
         * An auto-height field on a float page. It kept its drawn box and reported how much of
         * its value did not fit, so the box is grown by that much, the same way a blur grows it
         * in the builder. nextFieldPosition rather than fieldPosition, so the calculator sees
         * the lineHeight the field is actually drawn with once file styles have applied.
         */
        const changes = resolveAutoHeightExportChanges({
          fieldPosition: nextFieldPosition,
          measurement,
          rowHeight,
          presentation: fieldPresentationMode,
        });

        if (changes) Object.assign(nextFieldPosition, changes);

      } else if (measurement) {

        /**
         * Grid pages. Already converted from pixels into grid rows by ExportFormPage, which is
         * the same unit fieldPosition.height uses, so the two can be compared directly.
         */
        const measuredHeight = measurement.height;

        /**
         * Single line text fields are floored at the height the designer drew, so measuring
         * can only ever make the box taller. See resolveMeasuredFieldHeight for why.
         */
        nextFieldPosition.height = resolveMeasuredFieldHeight(fieldPosition.type, fieldPosition.height, measuredHeight);

      }

      return nextFieldPosition;

    });

  }
);

export default React.memo(JoyDocExporter);

